import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const PatentsSection = styled.section`
  padding: 100px 10%;
  background-color: var(--color-bg-alt);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 80px 5%;
  }
  
  @media (max-width: 576px) {
    padding: 60px 5%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:after {
    content: '';
    position: absolute;
    width: 50px;
    height: 3px;
    background-color: var(--color-primary);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
    margin-bottom: 1.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: var(--color-secondary);
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 0 auto 3rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin: 0 auto 2.5rem;
  }
`;

const PatentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PatentCardStyled = styled(motion.div)`
  background: var(--color-card);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 8px 25px var(--color-shadow);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;
  
  /* Animated background pattern */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(108, 99, 255, 0.05) 50%,
      transparent 70%
    );
    transform: rotate(-45deg) translateX(-100%);
    transition: transform 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-10px) rotateX(5deg);
    box-shadow: 0 15px 40px var(--color-shadow);
    border-color: var(--color-primary);
    
    &::before {
      transform: rotate(-45deg) translateX(100%);
    }
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem;
  }
`;

const PatentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const PatentIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
  margin-right: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(45deg, var(--color-primary), var(--color-secondary), var(--color-primary));
    border-radius: 14px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
    margin-right: 0.8rem;
  }
`;

const PatentHeaderText = styled.div`
  flex: 1;
`;

const PatentStatus = styled(motion.span)`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  background: ${props => props.status === 'Issued' 
    ? 'linear-gradient(135deg, #10b981, #059669)' 
    : 'linear-gradient(135deg, #f59e0b, #d97706)'};
  color: white;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 576px) {
    font-size: 0.7rem;
    padding: 0.25rem 0.6rem;
  }
`;

const PatentNumber = styled.p`
  font-size: 0.85rem;
  color: var(--color-secondary);
  margin: 0.3rem 0 0 0;
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.8rem;
  }
`;

const PatentTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-weight: 600;
  line-height: 1.4;
  
  @media (max-width: 576px) {
    font-size: 1.1rem;
  }
`;

const PatentDescription = styled.p`
  font-size: 0.95rem;
  color: var(--color-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  text-align: left;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

// const PatentFooter = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   padding-top: 1rem;
//   border-top: 1px solid rgba(255, 255, 255, 0.1);
  
//   @media (max-width: 576px) {
//     flex-direction: column;
//     gap: 0.5rem;
//     align-items: flex-start;
//   }
// `;

// const PatentDate = styled.p`
//   font-size: 0.85rem;
//   color: var(--color-primary);
//   margin: 0;
//   font-weight: 500;
  
//   @media (max-width: 576px) {
//     font-size: 0.8rem;
//   }
// `;

// const PatentInventors = styled.p`
//   font-size: 0.8rem;
//   color: var(--color-secondary);
//   margin: 0;
//   font-style: italic;
  
//   @media (max-width: 576px) {
//     font-size: 0.75rem;
//   }
// `;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-top: -0.5rem;
  align-self: flex-start;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--color-secondary);
  }
`;

// New PatentCard Component
const PatentCard = ({ patent, cardVariants, iconVariants, statusVariants }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <PatentCardStyled
      variants={cardVariants}
      whileHover={{ 
        scale: 1.02,
        rotateY: 2,
        transition: { duration: 0.3 }
      }}
      whileTap={{ scale: 0.98 }}
    >
      <PatentHeader>
        <PatentIcon
          variants={iconVariants}
          whileHover={{ 
            rotate: 0,
            scale: 1.1,
            transition: { duration: 0.3 }
          }}
        >
          💡
        </PatentIcon>
        <PatentHeaderText>
          <PatentStatus 
            status={patent.status}
            variants={statusVariants}
            whileHover={{
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
          >
            {patent.status}
          </PatentStatus>
          <PatentNumber>{patent.patentId}</PatentNumber>
        </PatentHeaderText>
      </PatentHeader>
      
      <PatentTitle>{patent.title}</PatentTitle>
      <PatentDescription style={isExpanded ? {
          display: 'block',
          WebkitLineClamp: 'unset'
        } : {}}>
        {patent.description}
      </PatentDescription>
      <ExpandButton
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? "Collapse" : "Expand"}
      </ExpandButton>
    </PatentCardStyled>
  );
};

const Patents = () => {
  const patentsData = [
    {
      title: "Touch input device and keyboard device for electronic devices",
      patentId: "US US10642385B1",
      status: "Issued",
      date: "Filed Jun 2017, Issued May 2020",
      description: "A keyboard device and a touch input device are disclosed. The keyboard device may include several keys designed to generate a command to a computing system, and the touch input device may include a touch input surface designed to generate a touch input by contact with the touch input surface. In addition to carrying several keys, the keyboard device is designed to magnetically couple to the touch input device. Further, the keyboard device may include a transmitter coil and the touch input device may include a receiver coil such that the keyboard device can transmit power to the touch input device by inductive power transmission. The inductive charging can be used to power i) the touch input device, ii) a battery of the touch input device, and/or iii) an electronic device (e.g., smartphone, wearable device, or handheld device) located on the touch input surface.",
      link: "https://patents.google.com/patent/US10642385B1/en"
    },
    {
      title: "Wireless Communication Device with Enhanced Antenna Performance",
      patentId: "US P34897US1",
      status: "Issued",
      date: "Filed Jun 2017",
      description: "A wireless communication device with an enhanced antenna system that dynamically optimizes signal quality and power efficiency based on environmental conditions and usage patterns. The patent covers innovative techniques for beam forming, signal processing, and antenna configuration that significantly improve connectivity in challenging environments.",
      link: null
    },
    {
      title: "Multimode Transceiver with Dynamic Power Allocation",
      patentId: "Confidential",
      status: "Pending",
      date: "Filed 2019",
      description: "An integrated circuit for wireless communications featuring dynamic power allocation across multiple wireless standards. The system determines optimal power distribution based on real-time signal requirements, enabling substantial battery savings while maintaining high quality connections across LTE, 5G NR, WiFi, and Bluetooth technologies.",
      link: null
    }
  ];

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9,
      rotateX: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const iconVariants = {
    hidden: { 
      scale: 0,
      rotate: -180
    },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.3
      }
    }
  };

  const statusVariants = {
    hidden: { 
      opacity: 0,
      x: 20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        delay: 0.5
      }
    }
  };

  return (
    <PatentsSection id="patents" ref={sectionRef}>
      <SectionTitle>Patents & Innovations</SectionTitle>
      <SectionSubtitle>
        Intellectual property and technology innovations I've developed
      </SectionSubtitle>
      
      <PatentsGrid
        as={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {patentsData.map((patent) => (
          <PatentCard 
            key={patent.title} 
            patent={patent} 
            cardVariants={cardVariants}
            iconVariants={iconVariants}
            statusVariants={statusVariants}
          />
        ))}
      </PatentsGrid>
    </PatentsSection>
  );
};

export default Patents;